import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CopyId } from "@/components/copy-id";
import { type UserBadgeGrant, badgesProvider } from "@/providers/badges.provider";
import { cardsProvider } from "@/providers/cards.provider";
import { type User, usersProvider } from "@/providers/users.provider";
import {
  IconChevronLeft,
  IconChevronRight,
  IconMedal,
  IconPhoto,
  IconSearch,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BadgeFilterPreviewCanvas } from "./badges";

const PAGE_SIZE = 50;

function UserStatusBadge({ user }: { user: User }) {
  if (user.IsDeleted) return <Badge variant="destructive">Deleted</Badge>;
  if (user.IsActive)
    return (
      <Badge variant="outline" className="border-green-500 text-green-600">
        Active
      </Badge>
    );
  return <Badge variant="secondary">Inactive</Badge>;
}

function UserRow({ user, onClick }: { user: User; onClick: () => void }) {
  const fullName =
    [user.FirstName, user.LastName].filter(Boolean).join(" ") || "—";
  const dob = user.DateOfBirth
    ? new Date(user.DateOfBirth).toLocaleDateString()
    : "—";
  const createdAt = new Date(user.CreatedAt).toLocaleDateString();

  return (
    <TableRow
      className={`cursor-pointer ${user.IsDeleted ? "opacity-50" : ""}`}
      onClick={onClick}
    >
      <TableCell>
        <CopyId id={user.UserId} />
      </TableCell>
      <TableCell className="font-medium">{user.Username}</TableCell>
      <TableCell>{fullName}</TableCell>
      <TableCell className="text-muted-foreground">{user.Email}</TableCell>
      <TableCell className="text-muted-foreground">{dob}</TableCell>
      <TableCell className="text-muted-foreground text-xs">
        {createdAt}
      </TableCell>
      <TableCell className="tabular-nums">
        {(user.Credits ?? 0).toLocaleString()}
      </TableCell>
      <TableCell className="tabular-nums">
        {(user.Xp ?? 0).toLocaleString()}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <UserStatusBadge user={user} />
        </div>
      </TableCell>
    </TableRow>
  );
}

function BlockedUserRow({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => usersProvider.getById(userId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-between py-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-48" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-2 text-sm gap-4">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-medium">
          {isError ? "Unknown user" : data?.Username}
        </span>
        {!isError && data?.Email && (
          <span className="text-xs text-muted-foreground truncate">
            {data.Email}
          </span>
        )}
      </div>
      <span className="font-mono text-xs text-muted-foreground shrink-0">
        {userId}
      </span>
    </div>
  );
}

function UserDetailSheet({
  user,
  onClose,
}: {
  user: User | null;
  onClose: () => void;
}) {
  const {
    data: blockedIds,
    isLoading: blocksLoading,
    isError: blocksError,
  } = useQuery({
    queryKey: ["user-blocks", user?.UserId],
    queryFn: () => usersProvider.getBlocks(user!.UserId),
    enabled: !!user,
  });

  const {
    data: userCards,
    isLoading: cardsLoading,
    isError: cardsError,
  } = useQuery({
    queryKey: ["user-cards", user?.UserId],
    queryFn: () => cardsProvider.getCardsForUser(user!.UserId),
    enabled: !!user,
  });

  const {
    data: userBadges,
    isLoading: badgesLoading,
    isError: badgesError,
  } = useQuery({
    queryKey: ["user-badges", user?.UserId],
    queryFn: () => badgesProvider.getForUser(user!.UserId),
    enabled: !!user,
  });

  const fullName = user
    ? [user.FirstName, user.LastName].filter(Boolean).join(" ") || "—"
    : "";
  const dob = user?.DateOfBirth
    ? new Date(user.DateOfBirth).toLocaleDateString()
    : "—";
  const createdAt = user ? new Date(user.CreatedAt).toLocaleDateString() : "";

  return (
    <Sheet
      open={!!user}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {user && (
          <>
            <SheetHeader>
              <SheetTitle>{user.Username}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {user.UserId}
              </SheetDescription>
            </SheetHeader>

            <div className="px-4 flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <UserStatusBadge user={user} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Name</span>
                <span>{fullName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Email</span>
                <span>{user.Email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Date of Birth</span>
                <span>{dob}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Joined</span>
                <span>{createdAt}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Credits</span>
                <span className="tabular-nums">
                  {(user.Credits ?? 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">XP</span>
                <span className="tabular-nums">
                  {(user.Xp ?? 0).toLocaleString()}
                </span>
              </div>
            </div>

            <Separator />

            <div className="px-4 flex flex-col gap-2">
              <h3 className="font-semibold text-sm">
                Blocked Users
                {!blocksLoading && !blocksError && (
                  <span className="ml-2 text-muted-foreground font-normal">
                    ({blockedIds?.length ?? 0})
                  </span>
                )}
              </h3>

              {blocksLoading ? (
                <div className="flex flex-col gap-2 mt-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-2"
                    >
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                  ))}
                </div>
              ) : blocksError ? (
                <p className="text-sm text-muted-foreground">
                  Failed to load blocks.
                </p>
              ) : !blockedIds?.length ? (
                <p className="text-sm text-muted-foreground">
                  This user hasn't blocked anyone.
                </p>
              ) : (
                <div className="flex flex-col divide-y">
                  {blockedIds.map((id) => (
                    <BlockedUserRow key={id} userId={id} />
                  ))}
                </div>
              )}
            </div>

            <Separator />

            <div className="px-4 flex flex-col gap-2">
              <h3 className="font-semibold text-sm">
                Profile Cards
                {!cardsLoading && !cardsError && (
                  <span className="ml-2 text-muted-foreground font-normal">
                    ({userCards?.length ?? 0})
                  </span>
                )}
              </h3>

              {cardsLoading ? (
                <div className="flex flex-col gap-2 mt-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : cardsError ? (
                <p className="text-sm text-muted-foreground">
                  Failed to load cards.
                </p>
              ) : !userCards?.length ? (
                <p className="text-sm text-muted-foreground">
                  This user has no cards.
                </p>
              ) : (
                <div className="flex flex-col divide-y rounded-md border text-xs">
                  {userCards.map((card) => (
                    <div
                      key={card.profile_card_id}
                      className="flex items-center gap-2 px-2 py-1.5"
                    >
                      {card.img_url ? (
                        <img
                          src={card.img_url}
                          alt={card.name}
                          className="size-5 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-5 rounded bg-muted border flex items-center justify-center shrink-0">
                          <IconPhoto className="size-3 text-muted-foreground" />
                        </div>
                      )}
                      <span className="flex-1">{card.name}</span>
                      <CopyId id={card.profile_card_id} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            <div className="px-4 flex flex-col gap-2">
              <h3 className="font-semibold text-sm">
                Badges
                {!badgesLoading && !badgesError && (
                  <span className="ml-2 text-muted-foreground font-normal">
                    ({userBadges?.length ?? 0})
                  </span>
                )}
              </h3>

              {badgesLoading ? (
                <div className="flex flex-col gap-2 mt-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : badgesError ? (
                <p className="text-sm text-muted-foreground">
                  Failed to load badges.
                </p>
              ) : !userBadges?.length ? (
                <p className="text-sm text-muted-foreground">
                  This user has no badges.
                </p>
              ) : (
                <div className="flex flex-col divide-y rounded-md border text-xs">
                  {userBadges.map((badge: UserBadgeGrant) => (
                    <div
                      key={badge.variant_id}
                      className="flex items-center gap-2 px-2 py-1.5"
                    >
                      <div className="shrink-0">
                        {badge.filter_img_url ? (
                          <BadgeFilterPreviewCanvas
                            baseImageUrl={badge.img_url}
                            filterImageUrl={badge.filter_img_url}
                            opacity={0.5}
                            size={24}
                          />
                        ) : badge.img_url ? (
                          <img
                            src={badge.img_url}
                            alt={badge.name}
                            className="size-6 rounded object-cover border"
                          />
                        ) : (
                          <div className="size-6 rounded bg-muted border flex items-center justify-center">
                            <IconMedal className="size-3.5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <span className="flex-1">{badge.name}</span>
                      {badge.variant_name !== "standard" && (
                        <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                          {badge.variant_name}
                        </span>
                      )}
                      <CopyId id={badge.badge_id} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setSearchPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const isSearching = debouncedSearch.length >= 1;

  const {
    data: users,
    isLoading,
    isError,
  } = useQuery({
    queryKey: isSearching
      ? ["users-search", debouncedSearch, searchPage]
      : ["users", page],
    queryFn: () =>
      isSearching
        ? usersProvider.search(debouncedSearch, searchPage)
        : usersProvider.list(page),
    staleTime: isSearching ? 30_000 : undefined,
  });

  const hasMore = (users?.length ?? 0) === PAGE_SIZE;

  function handleClearSearch() {
    setSearchInput("");
    setDebouncedSearch("");
    setSearchPage(1);
  }

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Users" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3">
          <div className="relative w-64">
            <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search by username, email or id…"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              className="pl-8 pr-8"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <IconX className="size-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconUsers className="size-4" />
            <span className="text-sm">
              {isLoading
                ? "Loading…"
                : users
                  ? isSearching
                    ? `${users.length} result${users.length !== 1 ? "s" : ""} on page ${searchPage}`
                    : `${users.length} user${users.length !== 1 ? "s" : ""} on page ${page}`
                  : ""}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Date of Birth</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>XP</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Failed to load users. Check your connection and try again.
                  </TableCell>
                </TableRow>
              ) : !users?.length ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {page > 1
                      ? "No more users on this page."
                      : "No users found."}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <UserRow
                    key={user.UserId}
                    user={user}
                    onClick={() => setSelectedUser(user)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {(() => {
          const activePage = isSearching ? searchPage : page;
          const setActivePage = isSearching ? setSearchPage : setPage;
          return activePage > 1 || hasMore ? (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={activePage === 1}
                onClick={() => setActivePage((p) => p - 1)}
                className="gap-1"
              >
                <IconChevronLeft className="size-3.5" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {activePage}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore}
                onClick={() => setActivePage((p) => p + 1)}
                className="gap-1"
              >
                Next
                <IconChevronRight className="size-3.5" />
              </Button>
            </div>
          ) : null;
        })()}
      </div>

      <UserDetailSheet
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}
